import {
  type PlanarPaneContent,
  ViewMode,
  ViewModeCapabilityCoordinator,
} from '@mobile-mapping/web3d';
import { Web3DViewer } from '@technology/web3d';
import { AlignmentNavigationToolbar } from '@trimble/alignment-navigation';
import {
  BasicButton,
  CameraControlSelector,
  TrbToolbar,
  ToolbarPositioner,
  Anchor,
} from '@trimble/ui-components/solidjs';
import { t } from '@trimble/i18n';
import { FormattingSettings } from '@trimble/types';
import { PaneToolbarVisibilityControl } from './PaneToolbarVisibilityControl';
import { ObjectSelectionToolbarContainer } from './ObjectSelectionToolbarContainer';
import {
  CapabilitiesCollection,
  CapabilityRegistryContainer,
} from '@trimble/web3d';
import {
  AlignmentNavigationCapability,
  FreeViewCapability,
  ImageCapabilityBase,
  SelectionActionsCapability,
  StationsNavigationCapability,
} from '@trimble/web3d-capabilities';
import { Accessor, Component, createMemo, Show } from 'solid-js';

import { createRotatePanoramasAction } from '../station-load/rotate-panoramas-action';
import { PaneBottomBar } from './PaneBottomBar';

// CameraControlSelector requires AlignmentNavigationCapability, but rotate /
// pan / look-around only need FreeViewCapability. Alignment is registered
// after async 3D setup
const inertAlignmentNav = {
  isEnabled: (): boolean => false,
  isActive: (): boolean => false,
  toggleAlignmentNavigationTool: (_crossSection: boolean): Promise<void> =>
    Promise.resolve(),
  setCrossSectionEnabled: (_enabled: boolean): void => undefined,
} as unknown as AlignmentNavigationCapability;

export interface PaneViewerChromeProps {
  readonly viewer: Accessor<Web3DViewer | undefined>;
  readonly viewModeType: Accessor<ViewMode['type'] | undefined>;
  readonly viewModeCapCoord: Accessor<
    ViewModeCapabilityCoordinator | undefined
  >;
  readonly capRegContainer: CapabilityRegistryContainer;
  readonly allowPopup: boolean;
  readonly formattingSettings: Accessor<FormattingSettings>;
  readonly planarContents: Accessor<readonly PlanarPaneContent[]>;
}

export const PaneViewerChrome: Component<PaneViewerChromeProps> = (props) => {
  const selectionActionsCapability = ():
    | SelectionActionsCapability
    | undefined => props.capRegContainer.get(SelectionActionsCapability)();

  // The top floating toolbar hosts the per-pane visibility dropdown plus
  // the 3D-only camera controls. It stays visible
  // in every view mode so the visibility dropdown is always reachable — in
  // 2D map mode the dropdown surfaces the frustum / stationMarkers /
  // navigationPath toggles (see `buildRowsForViewMode`), while the 3D-only
  // controls inside are gated separately (see `viewModeType() === '3D'`
  // below) so they don't clutter 2D / panorama / image panes, where there's
  // no camera-mode switching.
  const isFloatingToolbarVisible = createMemo(
    () => props.viewModeType() != null
  );

  // Close leaves station-nav in 3D only, so 3D panes keep the bar while a
  // station is selected.
  const isStationNavBarVisible = createMemo(() => {
    if (!props.viewModeCapCoord()?.stationNavigationController()) return false;
    return (
      props.viewModeType() === 'Image' ||
      props.viewModeType() === 'Panorama' ||
      props.capRegContainer
        .get(StationsNavigationCapability)()
        ?.selectedStationIndex() != null
    );
  });

  const viewToolActions = createRotatePanoramasAction({
    viewModeType: () => props.viewModeType(),
    planarContents: () => props.planarContents(),
  });

  const isImageLoading = createMemo(() => {
    const vm = props.viewModeCapCoord()?.viewMode();
    if (vm?.type !== 'Image' && vm?.type !== 'Panorama') return false;

    const imageCaps = props.capRegContainer.get(
      CapabilitiesCollection<ImageCapabilityBase>
    )();
    if (!imageCaps) return false;

    const cameraIndex = vm.type === 'Image' ? vm.cameraIndex : 0;
    return imageCaps.capabilities[cameraIndex]?.isImageLoading() ?? false;
  });

  return (
    <>
      <Show when={isImageLoading()}>
        <div
          class="pointer-events-none absolute inset-0 z-header flex items-center justify-center"
          data-testid="image-texture-loading-spinner"
        >
          <modus-wc-loader size="lg" variant="spinner" />
        </div>
      </Show>

      <Show when={props.viewer()}>
        <Show when={isFloatingToolbarVisible()}>
          <ToolbarPositioner anchor={Anchor.TopCenter} autoHide>
            <TrbToolbar orientation="horizontal" addSeparators={false}>
              <Show when={props.viewModeType() === '3D'}>
                <Show when={props.capRegContainer.get(FreeViewCapability)()}>
                  {(freeView) => (
                    <CameraControlSelector
                      alignmentNavigationCapability={
                        props.capRegContainer.get(
                          AlignmentNavigationCapability
                        )() ?? inertAlignmentNav
                      }
                      freeViewCapability={freeView()}
                    />
                  )}
                </Show>
                <Show when={selectionActionsCapability()}>
                  <div
                    data-testid="main-toolbar-show-all-models"
                    class="contents"
                  >
                    <BasicButton
                      iconName="tc-icon-show-all"
                      tooltip={t('web3D.toolbar.actions.showAll')}
                      action={() =>
                        void selectionActionsCapability()?.showAll()
                      }
                      size="small"
                    />
                  </div>
                </Show>
              </Show>
              <PaneToolbarVisibilityControl
                capRegContainer={props.capRegContainer}
                viewMode={() =>
                  props.viewModeCapCoord()?.viewMode() ?? {
                    type: '3D' as const,
                  }
                }
              />
            </TrbToolbar>
          </ToolbarPositioner>
        </Show>
        <Show when={props.capRegContainer.get(AlignmentNavigationCapability)()}>
          <ToolbarPositioner anchor={Anchor.TopRight} autoHide>
            <AlignmentNavigationToolbar
              alignmentNavigationCapability={props.capRegContainer.get(
                AlignmentNavigationCapability
              )()}
              lengthUnit={props.formattingSettings().lengthUnit}
              lengthDecimals={props.formattingSettings().lengthDecimals}
            />
          </ToolbarPositioner>
        </Show>
        <PaneBottomBar
          viewModeCoordinator={props.viewModeCapCoord}
          viewToolActions={viewToolActions}
          isStationNavVisible={isStationNavBarVisible}
          showStationNavClose={() =>
            props.viewModeType() !== 'Image' &&
            props.viewModeType() !== 'Panorama'
          }
          allowPopup={props.allowPopup}
        />
        <Show when={props.viewModeType() === '3D'}>
          <ToolbarPositioner
            anchor={Anchor.BottomCenter}
            customClasses={isStationNavBarVisible() ? '!bottom-[4.5rem]' : ''}
          >
            <ObjectSelectionToolbarContainer
              capabilityRegistryContainer={props.capRegContainer}
              customClasses="!p-1"
            />
          </ToolbarPositioner>
        </Show>
      </Show>
    </>
  );
};
